// A puzzle, played: the solver's moves checked against the lines, the
// opponent's replies played, a wrong move answered, the line after solving
// played on and stepped through, a move of one's own tried and the engine
// asked about it, the position on the board scored.
//
// The puzzle knows no DOM. It moves a board through `Surface` — positions,
// marks, whether it takes a move — and says what the screen should show as a
// snapshot, which a component renders (`PuzzleBoard.tsx` and the screen's
// bars). What happens to the solver is told to the screen through `Host`.
// Everything here runs in Node too, against a surface that only records, and
// is tested there (tests/web/puzzle.test.mjs).

import { Chess, type Move } from "chess.js";
import * as Lines from "./lines.ts";
import type { Branch, Epilogue, Fault, Refutation, Score, Slot } from "./lines.ts";
import { formatScore, outcome, verdict, whitePov, winChances } from "./scores.ts";

// --- WHAT THE BOARD IS ASKED TO DO ---

/** The marks a board can wear. */
export type Mark =
  | "good" // a move that was right, or the opponent's reply: green
  | "source" // the square a move is being made from: yellow
  | "bad" // a move that lost: yellow on both squares
  | "check"; // the king in check: red

export type HintLevel = "square" | "arrow" | "clear";

/** The board the puzzle is played on. */
export interface Surface {
  /** Show a position; resolves when it is drawn. */
  setPosition(fen: string, animate: boolean): Promise<void>;
  mark(mark: Mark, squares: string[]): void;
  /** Take marks off: one kind, or every kind and the hint with them. */
  unmark(mark?: Mark): void;
  /** Up to three moves, told apart by shade: their pieces, or the moves. */
  hint(level: HintLevel, moves: string[]): void;
  /** Whether the board takes a move now, and whose. */
  setInput(input: "solver" | "free" | null): void;
}

export type Tone = "neutral" | "correct" | "error" | "checkmate";

export interface MoveInfo {
  by: "you" | "opponent";
  uci: string;
  san: string;
  correct?: boolean;
  index: number;
}

/** A move that was not the answer: said while it is played out, and once
 *  more when it is over. */
export interface WrongInfo {
  phase: "playing" | "done";
  punished: boolean;
  san: string;
  line?: string[];
  eval?: string;
  verdict?: string;
}

export interface EngineAnswer {
  line?: string[];
  white_eval?: Score | null;
  error?: string;
}

/** The screen the puzzle is on. */
export interface Host {
  onMove?(move: MoveInfo): void;
  onWrong?(wrong: WrongInfo): void;
  onSolved?(): void;
  onFault?(fault: Fault & { fen: string; moves: string }): void;
  /** The engine's line from a position; present where there is an engine. */
  askEngine?(fen: string): Promise<EngineAnswer>;
  /** What a position is worth, from White's side, or null. */
  askScore?(fen: string): Promise<Score | null>;
}

export interface AnswerCheck {
  best?: Score | null;
  tolerance: number;
}

export interface PuzzleOptions {
  fen: string;
  /** The solution as the server writes it: SAN or UCI, [variations], {alternatives}. */
  moves: string;
  refutations?: Refutation[];
  epilogues?: Epilogue[];
  answerCheck?: AnswerCheck | null;
  /** Once solved, the board stays live: moves can be tried and the engine asked. */
  explore?: boolean;
  /** Milliseconds for each pause; the tests set them all to nothing. */
  timing?: Partial<Timing>;
}

export interface Timing {
  /** Before the opponent replies to a right move. */
  reply: number;
  /** A variation done, before the board goes back for the next. */
  lineDone: number;
  /** Back at the fork, before the opponent plays the other reply. */
  fork: number;
  /** Before a played-out line starts: the move that set it off has only just
   *  landed, and without a pause the board answers before it is read. */
  lead: number;
  /** Each move of a played-out line: slow enough to read the position. */
  step: number;
  /** Before the engine's line starts, once its answer is in. */
  engineLead: number;
  /** Each move of the engine's line. */
  engineStep: number;
  /** How long a position stays before its score is asked for: stepping
   *  quickly passes through positions nobody stopped to look at. */
  settle: number;
}

const TIMING: Timing = { reply: 500, lineDone: 1500, fork: 800, lead: 2200, step: 1200, engineLead: 120, engineStep: 900, settle: 450 };

/** How much of the engine's principal variation is played out: long enough
 *  to show what the move comes to, short enough that the end still matters. */
const ENGINE_LINE_PLIES = 6;

// --- WHAT THE SCREEN SHOWS ---

export interface Step {
  san: string;
  fen: string;
  yours: boolean;
}

export type Phase =
  | "solving" // the solver's move, or the opponent's reply on its way
  | "punishing" // a losing move being played out
  | "punished" // played out; Try again takes it back
  | "accepted" // a "losing" move that holds, taken
  | "solved" // every line played
  | "broken"; // a line that cannot go on

export interface Snapshot {
  phase: Phase;
  status: { text: string; tone: Tone };
  /** "Variation 1 of 2", when there is more than one to play. */
  info: string;
  /** What the line came to: the verdict on a wrong move, or what solving won. */
  aftermath: { score: string; text: string; good: boolean } | null;
  /** The line once it is over, to be stepped through. */
  replay: { steps: Step[]; at: number; along: number; canBack: boolean; canOn: boolean } | null;
  /** The two ways the line can go on: what holds, and what people play. */
  continuations: { best: string; common: string; which: "best" | "common" } | null;
  /** A variation of one's own, or the engine's, off the line. Null until the
   *  puzzle is over and the board is free. */
  explore: {
    steps: Step[];
    at: number;
    engine: boolean;
    thinking: boolean;
    note: string;
    /** Whether the engine can be asked from where the board stands. */
    canAsk: boolean;
  } | null;
  /** What the position on the board is worth, always, from White's side. */
  score: { text: string; whiteShare: number };
  /** Whether a hint has anything to show. */
  hintable: boolean;
}

// --- THE PUZZLE ---

/** Said once per page for each thing, however often it recurs. */
const faultsSaid = new Set<string>();

type Timer = ReturnType<typeof setTimeout>;

export class Puzzle {
  readonly fen: string;
  readonly movesString: string;
  readonly branches: Branch[];
  readonly timing: Timing;

  // The solving. These names are what the tests read (tests/web/board.mjs).
  game: Chess;
  currentBranchIdx = 0;
  solved: number[] = [];
  solution: Slot[];
  currentMoveIdx = 0;
  isPlayerTurn = true;
  waitingForOpponent = false;
  punishing = false;
  retryFen: string | null = null;
  finished = false;
  phase: Phase = "solving";

  refutations: Record<string, Refutation> = {};
  epilogues: Epilogue[] = [];
  answerCheck: AnswerCheck | null;
  canExplore: boolean;

  // After solving: the line, stepped through.
  epilogue: Epilogue | null = null;
  continuation: "best" | "common" = "best";
  replay: Step[] | null = null;
  replayAt = 0;

  // Trying a move that was never played. `exploreFrom` is the step of the
  // line it branches off; `exploreAt` how many of its moves are on the board.
  exploring = false;
  exploreFrom = 0;
  exploreSteps: Step[] = [];
  exploreAt = 0;
  /** Counts everything that moves the board, so a line being played out
   *  knows when it has been walked away from. */
  exploreRun = 0;
  exploreWaitingFor: string | null = null;
  exploreNote = "";

  // How the board got where it is. `game` is reloaded whenever the puzzle
  // rewinds itself — the next variation, a losing move taken back — and
  // forgets its history when it does, so what was played before the reload
  // is kept here and the two are read together (`played`).
  private baseMoves: string[] = [];
  private retryMoves: string[] = [];

  private surface: Surface | null = null;
  private host: Host;
  private statusText = "Your turn!";
  private statusTone: Tone = "neutral";
  private aftermath: Snapshot["aftermath"] = null;
  private scores = new Map<string, Score>();
  private scoreAsked = new Map<string, "asked" | "failed">();
  private scoreTimer: Timer | null = null;
  private scoreFor = "";
  private timers = new Set<Timer>();
  private listeners = new Set<() => void>();
  private snap: Snapshot | null = null;
  private destroyed = false;

  constructor(options: PuzzleOptions, host: Host = {}) {
    this.fen = options.fen;
    this.movesString = options.moves;
    this.host = host;
    this.timing = { ...TIMING, ...options.timing };
    const read = Lines.branchesOf(options.fen, options.moves);
    this.branches = read.branches;
    this.solution = this.branches[0]!.moves;
    this.game = new Chess(options.fen);
    this.answerCheck = options.answerCheck ?? null;
    this.canExplore = Boolean(options.explore && host.askEngine);

    for (const refutation of options.refutations || []) {
      if (refutation && refutation.uci) {
        const whole = Lines.standardLine(this.fen, [refutation.uci].concat(refutation.line || []));
        this.refutations[whole[0]!] = { ...refutation, uci: whole[0]!, line: whole.slice(1) };
      }
    }
    // A continuation starts where an answer ends, and says so with half a
    // position; the whole of it is found by playing the answers out.
    const ends = this.branches.map((b) => Lines.fenAfter(this.fen, b.moves.map(Lines.first))).filter((f): f is string => Boolean(f));
    this.epilogues = (options.epilogues || []).map((epilogue) => {
      const from = epilogue && epilogue.from && ends.find((fen) => Lines.positionKey(fen) === Lines.positionKey(epilogue.from));
      if (!from) return epilogue;
      return { ...epilogue, line: Lines.standardLine(from, epilogue.line), common: Lines.standardLine(from, epilogue.common) };
    });

    // The answer's score is the score of the question and of every position
    // of its solution, along every branch of it.
    const best = this.answerCheck?.best ? whitePov(this.fen, this.answerCheck.best) : null;
    this.knowScore(this.fen, best);
    for (const branch of this.branches) {
      const line = branch.moves.map(Lines.first);
      this.knowLine(this.fen, line, best);
      // Where an answer ends, its continuation begins, and that was searched
      // from there: its score is that position's own.
      const end = Lines.fenAfter(this.fen, line);
      const epilogue = end && this.epilogues.find((e) => e && e.from && Lines.positionKey(e.from) === Lines.positionKey(end));
      if (end && epilogue && epilogue.score) this.knowLine(end, epilogue.line || [], whitePov(this.fen, epilogue.score));
    }

    // Everything that is wrong with what arrived, said now rather than found
    // by someone pressing a button: the check the library is put through
    // before a deploy.
    for (const found of Lines.checkPuzzle({
      fen: this.fen,
      widget_moves: this.movesString,
      refutations: options.refutations || [],
      epilogues: options.epilogues || [],
    })) {
      this.fault(found.kind, found.detail);
    }
  }

  /** The side the solver plays. */
  get solver(): "w" | "b" {
    return Lines.sideOf(this.fen);
  }

  /** Put the puzzle on a board. */
  attach(surface: Surface): void {
    this.surface = surface;
    void surface.setPosition(this.shownFen(), false);
    this.applyInput();
    this.emit();
  }

  destroy(): void {
    this.destroyed = true;
    for (const timer of this.timers) clearTimeout(timer);
    this.timers.clear();
    if (this.scoreTimer) clearTimeout(this.scoreTimer);
    this.listeners.clear();
    this.surface = null;
  }

  // --- SUBSCRIBING ---

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): Snapshot => {
    if (!this.snap) this.snap = this.buildSnapshot();
    return this.snap;
  };

  private emit(): void {
    if (this.destroyed) return;
    this.snap = null;
    this.watchScore();
    for (const listener of this.listeners) listener();
  }

  private later(fn: () => void, ms: number): void {
    if (this.destroyed) return;
    const timer = setTimeout(() => {
      this.timers.delete(timer);
      if (!this.destroyed) fn();
    }, ms);
    this.timers.add(timer);
  }

  private setStatus(text: string, tone: Tone = "neutral"): void {
    this.statusText = text;
    this.statusTone = tone;
  }

  /** What the board takes: the solver's move, anything, or nothing. */
  private applyInput(): void {
    if (!this.surface) return;
    if (this.exploring) this.surface.setInput(this.exploreWaitingFor ? null : "free");
    else if (this.isPlayerTurn && !this.punishing && !this.finished && !this.waitingForOpponent && this.phase === "solving") this.surface.setInput("solver");
    else this.surface.setInput(null);
  }

  private show(fen: string, animate: boolean): Promise<void> {
    return this.surface ? this.surface.setPosition(fen, animate).catch(() => {}) : Promise.resolve();
  }

  private mark(mark: Mark, ...squares: string[]): void {
    this.surface?.mark(mark, squares);
  }

  private clearMarks(): void {
    this.surface?.unmark();
  }

  /** Castling moves two pieces, a pawn on the last rank becomes another, and
   *  one taken in passing is not where the hand went: the board moved one
   *  piece and the game moved two. */
  private resync(move: Move, fen: string): void {
    if (!/[kqep]/.test(move.flags)) return;
    this.later(() => void this.show(fen, false), 0);
  }

  private markCheck(game: Chess): void {
    if (!game.in_check()) return;
    const king = kingSquare(game, game.turn());
    if (king) this.mark("check", king);
  }

  // --- THE SOLVER'S MOVE ---

  /** Whether the piece on a square may be picked up. */
  canPickUp(square: string): boolean {
    if (this.exploring) {
      if (this.exploreWaitingFor) return false;
      const board = new Chess(this.exploreFen());
      const held = board.get(square);
      if (!held || held.color !== board.turn()) return false;
      this.mark("source", square);
      return true;
    }
    if (this.punishing || this.finished || this.phase !== "solving" || this.game.game_over() || !this.isPlayerTurn) return false;
    const piece = this.game.get(square);
    if (!piece || piece.color !== this.game.turn()) return false;
    this.mark("source", square);
    return true;
  }

  /** The piece was put down, or taken back to where it was. */
  dropped(from: string, to: string | null): boolean {
    if (!to) {
      this.surface?.unmark("source");
      return false;
    }
    const taken = this.exploring ? this.tryMove(from, to) : this.play(from, to);
    this.applyInput();
    this.emit();
    return taken;
  }

  private play(from: string, to: string): boolean {
    this.clearMarks();
    // Where to come back to if the move turns out to lose: taken before the
    // move so it works at any depth, not only at the puzzle's first ply.
    const fenBefore = this.game.fen();
    const move = this.game.move({ from, to, promotion: "q" });
    if (!move) return false;

    // Solutions record promotions in full ("b7b8q"), so the played move has
    // to carry the promotion piece too or it can never match. The board
    // always promotes to a queen.
    const uci = from + to + (move.promotion || "");
    let correct = Lines.accepts(this.solution[this.currentMoveIdx], uci);
    // The lines of an alternative are all correct answers, so playing the one
    // recorded in a sibling is right rather than wrong: follow it.
    if (!correct) {
      const sibling = Lines.siblingFor(this.branches, this.currentBranchIdx, this.solved, this.currentMoveIdx, uci);
      if (sibling >= 0) {
        this.currentBranchIdx = sibling;
        this.solution = this.branches[sibling]!.moves;
        correct = true;
      }
    }

    if (!correct) return this.wrong(move, uci, fenBefore);

    this.host.onMove?.({ by: "you", uci, san: move.san, correct: true, index: this.currentMoveIdx });
    this.currentMoveIdx++;
    this.mark("good", from, to);
    this.resync(move, this.game.fen());
    const mate = this.game.in_checkmate();
    this.markCheck(this.game);

    if (this.currentMoveIdx >= this.solution.length) {
      this.isPlayerTurn = false;
      this.lineDone(mate);
      return true;
    }
    this.setStatus(this.game.in_check() ? "Check! Right. Now the reply…" : "Right. Now the reply…", "correct");
    this.isPlayerTurn = false;
    this.waitingForOpponent = true;
    this.later(() => this.opponentMoves(), this.timing.reply);
    return true;
  }

  /** The end of a line: the next variation, or the end of the puzzle. */
  private lineDone(mate: boolean): void {
    this.waitingForOpponent = false;
    if (this.hasMoreBranches()) {
      this.setStatus(mate ? "Checkmate! That variation is done." : "That variation is done.", mate ? "checkmate" : "correct");
      // Nobody's move until the next variation is on the board: for this
      // moment the opponent is to move, and a board that still took input
      // would let their piece be picked up.
      this.isPlayerTurn = false;
      this.later(() => this.startNextBranch(), this.timing.lineDone);
    } else {
      this.setStatus(mate ? "Checkmate! Solved." : "Solved.", mate ? "checkmate" : "correct");
      this.finish();
    }
  }

  private chosen(): number[] {
    return this.solved.includes(this.currentBranchIdx) ? this.solved : this.solved.concat([this.currentBranchIdx]);
  }

  private hasMoreBranches(): boolean {
    return Lines.nextToSolve(this.branches, this.chosen()) !== null;
  }

  /** Back to where the next line parts from the last, not to the beginning. */
  private startNextBranch(): void {
    const previous = this.currentBranchIdx;
    if (!this.solved.includes(previous)) this.solved.push(previous);
    const next = Lines.nextToSolve(this.branches, this.solved);
    if (next === null) {
      this.setStatus("Solved.", "correct");
      this.finish();
      this.emit();
      return;
    }
    this.currentBranchIdx = next;
    // The next line is a variation of every line played, so it parts from
    // the last one at a move of the opponent's: back to just before it.
    const nextLine = this.branches[next]!.moves;
    const fork = Lines.partsAt(this.branches[previous]!.moves, nextLine);
    const at = Lines.fenAfter(this.fen, nextLine.slice(0, fork)) ?? this.fen;
    this.baseMoves = this.sansOf(nextLine.slice(0, fork).map(Lines.first));
    this.game = new Chess(at);
    this.solution = nextLine;
    this.currentMoveIdx = fork;
    this.waitingForOpponent = false;
    this.isPlayerTurn = false;
    this.applyInput();
    void this.show(at, true).then(() => {
      this.clearMarks();
      this.setStatus("Next variation…");
      this.emit();
      this.later(() => this.opponentMoves(), this.timing.fork);
    });
    this.emit();
  }

  private opponentMoves(): void {
    this.waitingForOpponent = false;
    if (this.currentMoveIdx >= this.solution.length) return;
    const uci = Lines.first(this.solution[this.currentMoveIdx]);
    const move = Lines.playUci(this.game, uci);
    if (!move) {
      // A reply that cannot be played used to be skipped without a word, and
      // the solver was asked to move again — for the side that had not.
      this.fault("reply-unplayable", `${uci} in ${this.game.fen()}`);
      this.stopBroken();
      return;
    }
    this.host.onMove?.({ by: "opponent", uci, san: move.san, index: this.currentMoveIdx });
    const fen = this.game.fen();
    void this.show(fen, true).then(() => {
      if (this.destroyed) return;
      this.clearMarks();
      this.mark("good", move.from, move.to);
      this.markCheck(this.game);
      this.currentMoveIdx++;
      if (this.game.in_checkmate()) {
        this.lineDone(true);
      } else {
        this.isPlayerTurn = true;
        this.setStatus(this.game.in_check() ? "Check! Your turn!" : "Your turn!");
      }
      this.applyInput();
      this.emit();
    });
  }

  /** The line cannot go on: say so on the board, and take no more moves. */
  private stopBroken(): void {
    this.isPlayerTurn = false;
    this.waitingForOpponent = false;
    this.phase = "broken";
    this.setStatus("This line cannot be played on from here. It has been reported.", "error");
    this.applyInput();
    this.emit();
  }

  // --- A MOVE THAT IS NOT THE ANSWER ---

  private wrong(move: Move, uci: string, fenBefore: string): boolean {
    // "Wrong" on its own teaches nothing in a blunder: the move looked
    // reasonable and the punishment is two moves away. When the engine's
    // answer to it came with the puzzle, show it instead of taking it back.
    const refutation = this.refutations[uci];
    const holds = Boolean(refutation && this.alsoHolds(refutation));
    this.host.onMove?.({ by: "you", uci, san: move.san, correct: holds, index: this.currentMoveIdx });
    if (refutation) {
      // The refutation carries the evaluation the move actually reaches, so
      // it can be checked against the answer about to be insisted on. When it
      // is no worse, the puzzle was wrong, and the only honest thing is to
      // take the move and say so.
      if (holds) return this.acceptAfterAll(refutation, move);
      this.punish(refutation, move, fenBefore);
      // The board keeps the piece where it was put; the line plays on from it.
      return true;
    }
    this.setStatus("Not that one. Try again.", "error");
    this.host.onWrong?.({ phase: "done", punished: false, san: move.san });
    this.game.undo();
    this.clearMarks();
    const fen = this.game.fen();
    this.later(() => void this.show(fen, false), 200);
    return false;
  }

  /**
   * Whether the move about to be punished in fact holds.
   *
   * Two searches decide a blunder puzzle — one wide enough to see every legal
   * move, one deep enough to trust — and where they disagree the report can
   * offer an answer at -0.23 while refuting a move that reaches -0.20. The
   * refutation carries that number; checking it here makes showing the
   * contradiction impossible rather than unlikely. The server cannot do this
   * for the board: the comparison only means anything against the number the
   * report puts on the card, and it has to run against reports written by
   * every earlier version of the detection.
   */
  private alsoHolds(refutation: Refutation): boolean {
    const check = this.answerCheck;
    if (!check || !check.best) return false;
    return winChances(check.best) - winChances(refutation.score) <= check.tolerance;
  }

  /** Take the move, say sorry, and end the puzzle there. */
  private acceptAfterAll(refutation: Refutation, move: Move): boolean {
    this.clearMarks();
    this.mark("good", move.from, move.to);
    this.resync(move, this.game.fen());
    this.setStatus(`${move.san} holds too — my mistake.`, "correct");
    const white = whitePov(this.fen, refutation.score);
    this.aftermath = { score: formatScore(white), text: outcome(refutation.score), good: true };
    this.knowScore(this.game.fen(), white);
    this.isPlayerTurn = false;
    this.waitingForOpponent = false;
    this.phase = "accepted";
    // Not `finish`: this move is not one of the answers, so no continuation
    // was computed for the position it reaches.
    this.host.onSolved?.();
    return true;
  }

  /**
   * Play the engine's answer to a losing move out on the board. The move
   * stays where the solver put it and the line runs on from there, so the
   * piece is seen coming off rather than described. `retryFen` is the
   * position to come back to, whenever the solver is ready.
   */
  private punish(refutation: Refutation, move: Move, fenBefore: string): void {
    this.punishing = true;
    this.phase = "punishing";
    this.isPlayerTurn = false;
    this.waitingForOpponent = false;
    this.retryFen = fenBefore;
    // The wrong move is on the board and the line that answers it is about
    // to be: what was played before it is where Retry comes back to.
    this.retryMoves = this.played().slice(0, -1);
    this.clearMarks();
    this.mark("bad", move.from, move.to);
    this.resync(move, this.game.fen());
    const intro = (moves: string[]) => `${move.san}? Watch:` + (moves.length ? " " + moves.join(" ") : "");
    this.setStatus(intro([]), "error");
    this.host.onWrong?.({ phase: "playing", punished: true, san: move.san });
    // The move is on the board already: what it is worth is what the line it
    // runs into is worth, at every step of it.
    this.knowLine(this.game.fen(), refutation.line || [], whitePov(this.fen, refutation.score));
    this.playLine({
      moves: refutation.line || [],
      mark: "bad",
      tone: "error",
      narrate: intro,
      fault: "punishment-unplayable",
      // Try again can be pressed while the line is still running.
      abandoned: () => !this.punishing,
      done: (played) => this.concludePunishment(refutation, [move.san].concat(played)),
    });
  }

  private concludePunishment(refutation: Refutation, played: string[]): void {
    if (!this.punishing) return;
    this.phase = "punished";
    const white = whitePov(this.fen, refutation.score);
    const said = verdict(refutation.score);
    this.aftermath = { score: formatScore(white), text: said, good: false };
    this.host.onWrong?.({ phase: "done", punished: true, san: played[0]!, line: played.slice(1), eval: formatScore(white), verdict: said });
    this.setStatus(`${played[0]}? Watch: ${played.slice(1).join(" ")}`, "error");
    this.emit();
  }

  /** Back to the position the losing move was played from. */
  retry(): void {
    if (!this.retryFen) return;
    const fen = this.retryFen;
    this.retryFen = null;
    this.punishing = false;
    this.phase = "solving";
    this.baseMoves = this.retryMoves;
    this.game = new Chess(fen);
    this.isPlayerTurn = true;
    this.waitingForOpponent = false;
    this.aftermath = null;
    this.clearMarks();
    this.setStatus("Your turn!");
    this.applyInput();
    this.emit();
    void this.show(fen, true).then(() => {
      this.clearMarks();
      this.applyInput();
    });
  }

  /**
   * Play a line out on the board, one move at a time: a refutation or a
   * continuation. They differ in the colour of the mark, in the words, and in
   * whether they can be called off.
   */
  private playLine(options: {
    moves: string[];
    mark: Mark;
    tone: Tone;
    narrate: (played: string[]) => string;
    fault: string;
    abandoned?: () => boolean;
    done: (played: string[]) => void;
  }): void {
    const played: string[] = [];
    let next = 0;
    const step = () => {
      if (options.abandoned?.()) return;
      if (next >= options.moves.length) return options.done(played);
      const uci = options.moves[next++]!;
      const move = Lines.playUci(this.game, uci);
      // Every line is replayed on the server before it is stored, and checked
      // again before a deploy, so this should not happen; if it ever does,
      // stop where the board is honest, and say so.
      if (!move) {
        this.fault(options.fault, `${uci} in ${this.game.fen()}`);
        return options.done(played);
      }
      played.push(move.san);
      void this.show(this.game.fen(), true).then(() => {
        if (options.abandoned?.()) return;
        this.clearMarks();
        this.mark(options.mark, move.from, move.to);
        this.setStatus(options.narrate(played), options.tone);
        this.emit();
        this.later(step, this.timing.step);
      });
    };
    this.later(step, this.timing.lead);
  }

  // --- SOLVED ---

  /**
   * The puzzle is solved. Tell the screen, then play on: stopping the moment
   * the last move lands is what makes a short puzzle unsatisfying, the reason
   * the move was worth finding still two moves away. So the engine's own
   * continuation runs on from the end of the line and the position it
   * reaches is named.
   */
  private finish(): void {
    // Over: nobody's move until the board is handed back for trying things.
    // Left as it was, the board still thought it was the solver's turn with
    // the opponent to move, and took the opponent's move as a wrong answer.
    this.finished = true;
    this.isPlayerTurn = false;
    this.applyInput();
    this.host.onSolved?.();
    // Only a continuation that starts from the position actually on the
    // board: moving the board first would take away the move just played.
    const epilogue = this.epilogueFrom(this.game.fen());
    if (!epilogue) {
      this.concludeSolved(null);
      return;
    }
    this.playLine({
      moves: epilogue.line || [],
      mark: "good",
      tone: "correct",
      narrate: (played) => "Solved. " + played.join(" "),
      fault: "punishment-unplayable",
      done: () => this.concludeSolved(epilogue),
    });
  }

  /** The continuation belonging to a position, if there is one. Matched on the
   *  position, so a branch, an alternative and the main line cannot borrow
   *  each other's follow-up. */
  private epilogueFrom(fen: string): Epilogue | null {
    const key = Lines.positionKey(fen);
    const found = this.epilogues.find((e) => Lines.positionKey(e.from) === key);
    return found && found.line && found.line.length ? found : null;
  }

  private concludeSolved(epilogue: Epilogue | null): void {
    this.phase = "solved";
    if (epilogue) {
      this.aftermath = { score: formatScore(whitePov(this.fen, epilogue.score)), text: outcome(epilogue.score), good: true };
    }
    this.epilogue = epilogue;
    this.continuation = "best";
    const steps = this.buildReplay("best");
    const yours = steps.filter((s) => s.yours).map((s) => s.san);
    this.setStatus(yours.length ? `Solved with ${yours.join(" ")}.` : "Solved.", "correct");
    this.showReplay(steps);
    if (this.canExplore) this.exploring = true;
    this.applyInput();
    this.emit();
  }

  /**
   * Every position of the line that was solved, from the puzzle onwards.
   * Rebuilt from the branch rather than from the board, because a branching
   * puzzle rewinds itself as it goes and `game` no longer remembers how it
   * got here.
   */
  private buildReplay(which: "best" | "common"): Step[] {
    const solution = (this.branches[this.currentBranchIdx]?.moves || []).map(Lines.first);
    const epilogue = this.epilogue;
    const after = !epilogue ? [] : which === "common" ? epilogue.common || [] : epilogue.line || [];
    const moves = solution.concat(after);
    const game = new Chess(this.fen);
    const steps: Step[] = [{ san: "", fen: this.fen, yours: false }];
    for (let index = 0; index < moves.length; index++) {
      const move = Lines.playUci(game, moves[index]!);
      if (!move) {
        // Everything after an unplayable move would be played from the wrong
        // position, so the line stops there, and says why.
        this.fault("replay-unplayable", `${moves[index]} in ${game.fen()}`);
        break;
      }
      // Your own moves are the even ones of the solution: the puzzle starts
      // on your turn, and everything after it is the engine playing on.
      steps.push({ san: move.san, fen: game.fen(), yours: index < solution.length && index % 2 === 0 });
    }
    return steps;
  }

  private showReplay(steps: Step[]): void {
    if (steps.length < 2) return;
    this.replay = steps;
    this.replayAt = steps.length - 1;
    // A variation hangs off a step of the line it was tried from, and this
    // is another line.
    this.exploreSteps = [];
    this.exploreAt = 0;
    this.exploreWaitingFor = null;
    this.exploreRun += 1;
  }

  // --- STEPPING THE LINE ---
  //
  // Back and on walk whatever is on the board, one move at a time: the line,
  // and the variation hanging off it — a move of yours, or the engine asked
  // to go on. Stepping never throws anything away. It used to: one press of
  // back after the engine had played six moves took all six off the board
  // and the button that asks for more with them, when what was wanted was the
  // move before. A variation goes when it is replaced by another or put aside
  // with the button that says so.

  /** Whether the board is where the variation branches off, or along it. */
  private onVariation(): boolean {
    return Boolean(this.exploreSteps.length && this.replayAt === this.exploreFrom);
  }

  stepBy(delta: number): void {
    if (!this.replay) return;
    if (this.onVariation()) {
      const at = this.exploreAt + delta;
      if (at > this.exploreSteps.length) return;
      if (at >= 0) return this.gotoVariation(at);
    }
    this.gotoStep(this.replayAt + delta);
  }

  /** Put the board on one position of the line. */
  gotoStep(at: number): void {
    if (!this.replay) return;
    this.exploreRun += 1;
    this.exploreWaitingFor = null;
    this.exploreAt = 0;
    this.exploreNote = "";
    this.replayAt = Math.max(0, Math.min(this.replay.length - 1, at));
    void this.show(this.replay[this.replayAt]!.fen, true).then(() => this.clearMarks());
    this.applyInput();
    this.emit();
  }

  /** Put the board on a move of the variation. */
  gotoVariation(at: number): void {
    if (!this.replay || !this.exploreSteps.length) return;
    this.exploreRun += 1;
    this.exploreWaitingFor = null;
    this.replayAt = this.exploreFrom;
    this.exploreAt = Math.max(0, Math.min(this.exploreSteps.length, at));
    const fen = this.exploreFen();
    this.exploreNote = endNote(new Chess(fen));
    void this.show(fen, true).then(() => this.clearMarks());
    this.applyInput();
    this.emit();
  }

  // --- WHAT THEY PLAY, BESIDE WHAT HOLDS ---
  //
  // An opening puzzle's continuation comes from the book, which knows the
  // move that holds and the move people at your level actually choose. They
  // are often not the same — after 8.Bxf7+ Kxf7 9.Ng5+ the commonest reply
  // walks into mate in two — and both are worth seeing. The line that holds
  // is played first; the crowd's is one press away.

  pickContinuation(which: "best" | "common"): void {
    if (!this.epilogue || this.continuation === which) return;
    const before = this.replay || [];
    this.continuation = which;
    const steps = this.buildReplay(which);
    const from = Math.max(0, steps.findIndex((step, i) => !before[i] || before[i]!.san !== step.san));
    this.showReplay(steps);
    // Walk from the fork rather than jumping, so the difference is watched
    // rather than looked for.
    let at = from;
    this.gotoStep(at);
    const walk = () => {
      if (this.continuation !== which || at >= steps.length - 1) return;
      at += 1;
      this.gotoStep(at);
      this.later(walk, this.timing.step);
    };
    this.later(walk, this.timing.step);
  }

  // --- TRYING A MOVE THAT WAS NEVER PLAYED ---
  //
  // A solved puzzle leaves a line on the board and a way to step back through
  // it. Standing on any position in it, the natural next thought is "and what
  // if I had played this instead?". So the board stays live: play a move and
  // ask the engine, for as long as you care to go on. Nothing here is graded
  // and nothing is kept.

  /** The position on the board right now. */
  exploreFen(): string {
    if (this.exploring && this.onVariation() && this.exploreAt > 0) return this.exploreSteps[this.exploreAt - 1]!.fen;
    const step = this.replay?.[this.replayAt];
    return step ? step.fen : this.fen;
  }

  /** The position that is on the board now, whatever put it there. */
  shownFen(): string {
    return this.replay ? this.exploreFen() : this.game.fen();
  }

  /** A line of UCI moves written as SAN, from the puzzle's own position. */
  private sansOf(ucis: string[]): string[] {
    const game = new Chess(this.fen);
    const sans: string[] = [];
    for (const uci of ucis) {
      const move = Lines.playUci(game, uci);
      if (!move) break;
      sans.push(move.san);
    }
    return sans;
  }

  /** The moves from the puzzle's position to what is on the board, while it
   *  is being solved: whatever was played before the last rewind, and
   *  whatever has been played since. */
  private played(): string[] {
    return this.baseMoves.concat(this.game.history());
  }

  /**
   * What is on the board and how it got there, for the formats the rest of
   * chess reads (share.ts). Mid-solve that is the moves played; once the
   * line is over it is the line as far as it has been stepped, and the
   * variation walked into from there.
   */
  shown(): { fen: string; start: string; moves: string[] } {
    if (!this.replay) return { fen: this.game.fen(), start: this.fen, moves: this.played() };
    const moves = this.replay.slice(1, this.replayAt + 1).map((step) => step.san);
    if (this.onVariation()) for (const step of this.exploreSteps.slice(0, this.exploreAt)) moves.push(step.san);
    return { fen: this.shownFen(), start: this.fen, moves };
  }

  /** A move of your own, from the position being read. Either side. */
  private tryMove(from: string, to: string): boolean {
    const board = new Chess(this.exploreFen());
    const move = board.move({ from, to, promotion: "q" });
    if (!move) return false;
    this.branchHere();
    this.exploreSteps.push({ san: move.san, fen: board.fen(), yours: true });
    this.exploreAt = this.exploreSteps.length;
    this.exploreNote = endNote(board);
    this.resync(move, board.fen());
    this.surface?.unmark();
    this.mark("good", from, to);
    return true;
  }

  /** A move is about to be added where the board stands. Along a variation it
   *  goes on from there and replaces what came after; on the line itself it
   *  begins a new variation from this step. */
  private branchHere(): void {
    this.exploreRun += 1;
    if (this.onVariation() && this.exploreAt > 0) {
      this.exploreSteps.length = this.exploreAt;
    } else {
      this.exploreSteps = [];
      this.exploreFrom = this.replayAt;
      this.exploreAt = 0;
    }
  }

  /** Hand the position to the engine and play out its line. */
  askEngine(): void {
    if (!this.exploring || this.exploreWaitingFor || !this.host.askEngine) return;
    const fen = this.exploreFen();
    if (new Chess(fen).game_over()) return;
    this.branchHere();
    this.exploreWaitingFor = fen;
    this.exploreNote = "thinking…";
    this.applyInput();
    this.emit();
    this.host.askEngine(fen).then(
      (answer) => this.receiveAnswer(fen, answer),
      (err: unknown) => this.receiveAnswer(fen, { error: String(err instanceof Error ? err.message : err) })
    );
  }

  private receiveAnswer(fen: string, answer: EngineAnswer): void {
    // An answer to a position since left behind answers nothing asked now.
    if (this.destroyed || !this.exploring || this.exploreWaitingFor !== fen) return;
    this.exploreWaitingFor = null;
    if (answer.error) {
      this.exploreNote = answer.error;
      this.applyInput();
      this.emit();
      return;
    }
    this.playEngineLine(fen, Lines.standardLine(fen, answer.line || []), answer.white_eval ?? null);
  }

  /** The engine's line, played a move at a time and left there. It plays both
   *  sides; when it runs out the board is yours again, wherever it stopped. */
  private playEngineLine(fen: string, fullLine: string[], white: Score | null): void {
    const board = new Chess(fen);
    const line = fullLine.slice(0, ENGINE_LINE_PLIES);
    this.knowLine(fen, line, white);
    let next = 0;
    const run = this.exploreRun;
    this.applyInput();
    const step = () => {
      // Anything that moved the board since — a step, a move, another
      // question — and this line is no longer what is being watched. What it
      // had played stays where it is.
      if (!this.exploring || this.exploreWaitingFor || run !== this.exploreRun) return;
      if (next >= line.length) {
        this.exploreNote = endNote(board);
        this.emit();
        return;
      }
      const uci = line[next++]!;
      const move = Lines.playUci(board, uci);
      if (!move) {
        // An engine answer the board cannot play: "Continue the line" doing
        // nothing at all, the first time, was this.
        this.fault("engine-line-unplayable", `${uci} in ${board.fen()}`);
        this.exploreNote = endNote(board);
        this.emit();
        return;
      }
      this.exploreSteps.push({ san: move.san, fen: board.fen(), yours: false });
      this.exploreAt = this.exploreSteps.length;
      this.exploreNote = "thinking…";
      this.emit();
      void this.show(board.fen(), true).then(() => {
        if (run !== this.exploreRun) return;
        this.surface?.unmark("good");
        this.mark("good", move.to);
        this.later(step, this.timing.engineStep);
      });
    };
    this.later(step, this.timing.engineLead);
  }

  /** Put the line back on the board. */
  leaveExplore(): void {
    const back = this.exploreSteps.length ? this.exploreFrom : this.replayAt;
    this.exploreSteps = [];
    this.exploreAt = 0;
    this.exploreWaitingFor = null;
    this.gotoStep(back);
  }

  // --- THE HINT ---
  //
  // Given on the board, where the eye is, in two steps: "square" marks the
  // piece each answering move is made with, which leaves where it goes to be
  // found; "arrow" draws the moves; "clear" takes the hint away. The marks
  // clear with the next move, like every other mark.

  /** The moves due now: the line's, and every other move that answers here. */
  answersNow(): string[] {
    if (!this.isPlayerTurn || this.finished || this.phase !== "solving") return [];
    const fen = this.game.fen();
    return Lines.answersAt(this.branches, this.solution, this.currentMoveIdx).map((raw) => Lines.standardLine(fen, [raw])[0]!);
  }

  hint(level: HintLevel, moves?: string[]): void {
    if (!this.surface) return;
    if (level === "clear") {
      this.surface.hint("clear", []);
      return;
    }
    const fen = this.game.fen();
    const shown = (moves ? moves.map((raw) => Lines.standardLine(fen, [raw])[0]!) : this.answersNow()).filter((u) => typeof u === "string" && u.length >= 4);
    if (!shown.length) return;
    this.surface.unmark();
    this.surface.hint(level, shown);
  }

  // --- WHAT THE POSITION ON THE BOARD IS WORTH ---
  //
  // Most scores are already known and cost nothing. A principal variation is
  // worth the same at every position along it, so the puzzle's best score is
  // the score of every position of its solution, a refutation's of every
  // position the punishment passes through, a continuation's likewise, and an
  // engine's answer comes with its own. What is left — a move tried by hand,
  // the line the crowd plays — is asked of the host, once per position, after
  // the position has stayed put for a moment.

  private knowScore(fen: string, white: Score | null | undefined): void {
    if (!fen || !white || typeof white.value !== "number") return;
    this.scores.set(Lines.positionKey(fen), white);
  }

  /** Every position of a line is worth `white` — except that a mate gets
   *  nearer: a move of the mating side's is one fewer left to make. */
  private knowLine(fromFen: string, ucis: readonly string[], white: Score | null | undefined): void {
    if (!white || typeof white.value !== "number") return;
    let game: Chess;
    try {
      game = new Chess(fromFen);
    } catch {
      return;
    }
    const matingMovesFirst = white.value > 0 === (game.turn() === "w");
    const along = (plies: number): Score => {
      if (white.kind !== "mate") return white;
      const made = matingMovesFirst ? Math.ceil(plies / 2) : Math.floor(plies / 2);
      const left = Math.max(1, Math.abs(white.value) - made);
      return { kind: "mate", value: white.value > 0 ? left : -left };
    };
    this.knowScore(fromFen, white);
    let plies = 0;
    for (const uci of ucis) {
      if (typeof uci !== "string" || uci.length < 4) break;
      if (!Lines.playUci(game, uci)) break;
      plies += 1;
      this.knowScore(game.fen(), along(plies));
    }
  }

  /** Ask for the score of the position on the board, once it has stayed. */
  private watchScore(): void {
    const fen = this.shownFen();
    const key = Lines.positionKey(fen);
    if (key === this.scoreFor) return;
    this.scoreFor = key;
    if (this.scoreTimer) clearTimeout(this.scoreTimer);
    this.scoreTimer = null;
    if (this.scores.has(key) || this.scoreAsked.has(key) || !this.host.askScore) return;
    if (new Chess(fen).game_over()) return;
    this.scoreTimer = setTimeout(() => {
      this.scoreTimer = null;
      if (this.destroyed || this.scoreFor !== key || !this.host.askScore) return;
      this.scoreAsked.set(key, "asked");
      this.host.askScore(fen).then(
        (white) => {
          if (white) this.knowScore(fen, white);
          else this.scoreAsked.set(key, "failed");
          this.snap = null;
          for (const listener of this.listeners) listener();
        },
        () => {
          this.scoreAsked.set(key, "failed");
          this.snap = null;
          for (const listener of this.listeners) listener();
        }
      );
    }, this.timing.settle);
  }

  private scoreNow(): Snapshot["score"] {
    const fen = this.shownFen();
    const key = Lines.positionKey(fen);
    const game = new Chess(fen);
    const known = this.scores.get(key);
    if (known) return { text: formatScore(known), whiteShare: Math.round((winChances(known) + 1) * 50) };
    if (game.in_checkmate()) {
      const whiteWon = game.turn() === "b";
      return { text: whiteWon ? "1–0" : "0–1", whiteShare: whiteWon ? 100 : 0 };
    }
    if (game.game_over()) return { text: formatScore({ kind: "cp", value: 0 }), whiteShare: 50 };
    return { text: this.scoreAsked.get(key) === "failed" || !this.host.askScore ? "–" : "…", whiteShare: 50 };
  }

  // --- SAYING WHAT WENT WRONG ---

  private fault(kind: string, detail: string): void {
    const key = `${this.fen}|${this.movesString}|${kind}|${detail}`;
    if (faultsSaid.has(key)) return;
    faultsSaid.add(key);
    console.error(`[board] ${kind}: ${detail}`);
    this.host.onFault?.({ kind, detail: String(detail || "").slice(0, 500), fen: this.fen, moves: this.movesString });
  }

  // --- THE SNAPSHOT ---

  private buildSnapshot(): Snapshot {
    const { at, of } = Lines.progress(this.branches, this.solved, this.currentBranchIdx);
    const onVariation = this.onVariation();
    const along = onVariation ? this.exploreAt : 0;
    const replay = this.replay
      ? {
          steps: this.replay,
          at: this.replayAt,
          along,
          canBack: onVariation ? !(along === 0 && this.replayAt === 0) : this.replayAt > 0,
          canOn: onVariation ? along < this.exploreSteps.length : this.replayAt < this.replay.length - 1,
        }
      : null;

    let continuations: Snapshot["continuations"] = null;
    const epilogue = this.epilogue;
    if (epilogue && (epilogue.common || []).length) {
      const best = this.buildReplay("best");
      const common = this.buildReplay("common");
      // Each line is named by the move where they part company.
      const fork = best.findIndex((step, i) => !common[i] || common[i]!.san !== step.san);
      const share = typeof epilogue.common_share === "number" ? ` ${Math.round(epilogue.common_share * 100)}%` : "";
      continuations = {
        best: `${fork >= 0 ? (best[fork]?.san ?? "") : ""} holds`,
        common: `${fork >= 0 ? (common[fork]?.san ?? "") : ""} is usual${share}`,
        which: this.continuation,
      };
    }

    const explore: Snapshot["explore"] = this.exploring
      ? {
          steps: this.exploreSteps,
          at: along,
          // A line nobody has put a move of their own into is the engine's.
          engine: !this.exploreSteps.some((s) => s.yours),
          thinking: Boolean(this.exploreWaitingFor),
          note: this.exploreNote,
          canAsk: !this.exploreWaitingFor && !new Chess(this.exploreFen()).game_over(),
        }
      : null;

    return {
      phase: this.phase,
      status: { text: this.statusText, tone: this.statusTone },
      info: of > 1 && !this.finished ? `Variation ${at} of ${of}` : "",
      aftermath: this.aftermath,
      replay,
      continuations,
      explore,
      score: this.scoreNow(),
      hintable: this.answersNow().length > 0,
    };
  }
}

/** What a position says for itself, when it says anything. */
function endNote(board: Chess): string {
  if (!board.game_over()) return "";
  return board.in_checkmate() ? "and that is mate." : "and that is a draw.";
}

function kingSquare(game: Chess, color: "w" | "b"): string | null {
  const board = game.board();
  for (let r = 0; r < 8; r++) {
    for (let c = 0; c < 8; c++) {
      const piece = board[r]?.[c];
      if (piece && piece.type === "k" && piece.color === color) return "abcdefgh"[c]! + (8 - r);
    }
  }
  return null;
}
